// Chakra imports
import {
  Badge,
  Box,
  Card,
  Divider,
  Flex,
  HStack,
  Link,
  SimpleGrid,
  Stack,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Tfoot,
  Th,
  Thead,
  Tr,
  useColorModeValue,
} from '@chakra-ui/react';

import { getMovement } from 'services/api.service';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { maskCurrency } from 'utils/number';
import { Balance, MovementsType, MovementsTypeDescription } from 'types/Movement';
import { StatusRender } from '.';
import { TypeRender } from '../balances/balances';
import { maskDocument } from 'utils/document';
import PullToRefresh from 'react-simple-pull-to-refresh';
import { queryClient } from 'index';
import CopyableText from 'components/CopyableText';
import { usePrivacy } from 'contexts/PrivacyContext';
import {
  SettlementState,
  creditedToOwner,
  earliestRelease,
  hasAnyRefund,
  settlementDays,
  settlementStateOf,
  sharedSettlement,
  sharedValue,
  toAmount,
} from './movementBreakdown';

const SETTLEMENT_BADGE: Record<SettlementState, { label: string; colorScheme: string }> = {
  canceled: { label: 'CANCELADO', colorScheme: 'red' },
  released: { label: 'LIBERADO', colorScheme: 'green' },
  waiting: { label: 'AGUARDANDO', colorScheme: 'orange' },
};

/** Linha ainda não liberada só tem data prevista: a hora não significa nada até acontecer. */
const releaseLabel = (state: SettlementState, releasedOn?: string | null) => {
  if (!releasedOn) return '---';
  return DateTime.fromISO(releasedOn).toFormat(state === 'waiting' ? 'dd/MM/yyyy' : 'dd/MM/yyyy HH:mm');
};

const settlementCaption = (settlement: { state: SettlementState; releasedOn: string | null }) => {
  if (settlement.state === 'canceled') return 'Todos cancelados';
  if (!settlement.releasedOn) return 'Liberação sem data definida';

  const date = releaseLabel(settlement.state, settlement.releasedOn);
  return settlement.state === 'released' ? `Todos liberados em ${date}` : `Todos liberam em ${date}`;
};

const Section: React.FC<{ title: string; caption?: string; children: React.ReactNode }> = ({
  title,
  caption,
  children,
}) => {
  const muted = useColorModeValue('gray.500', 'whiteAlpha.600');

  return (
    <Box py={4}>
      <Flex align="baseline" justify="space-between" gap={3} mb={3} flexWrap="wrap">
        <Text fontSize="md" fontWeight="semibold">
          {title}
        </Text>
        {caption && (
          <Text fontSize="sm" color={muted}>
            {caption}
          </Text>
        )}
      </Flex>
      {children}
    </Box>
  );
};

const Field: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => {
  const muted = useColorModeValue('gray.500', 'whiteAlpha.600');

  return (
    <Box minW={0}>
      <Text fontSize="xs" color={muted} textTransform="uppercase" letterSpacing="wide">
        {title}
      </Text>
      <Box fontSize="md">{children}</Box>
    </Box>
  );
};

/**
 * A aritmética do recebimento, em linha.
 *
 * Bruto, taxas e líquido saem dos campos do próprio movimento, então fecham sempre. O
 * "creditado na sua conta" só aparece quando diverge do líquido — é o caso do split enviado
 * a terceiro, o único em que `amountLiquid` deixa de ser o que cai na conta.
 */
const SummaryRow: React.FC<{ label: string; value: number | string; strong?: boolean }> = ({
  label,
  value,
  strong,
}) => {
  const muted = useColorModeValue('gray.600', 'whiteAlpha.700');

  return (
    <Flex justify="space-between" align="baseline" gap={4}>
      <Text fontSize="sm" color={strong ? undefined : muted} fontWeight={strong ? 'semibold' : undefined}>
        {label}
      </Text>
      <Text fontSize={strong ? 'lg' : 'sm'} fontWeight={strong ? 'bold' : 'medium'} whiteSpace="nowrap">
        {maskCurrency(value)}
      </Text>
    </Flex>
  );
};

const BalancesForMovement: React.FC<{
  balances?: Balance[];
  ownerId?: string;
  ownerName?: string;
}> = ({ balances, ownerId, ownerName }) => {
  const { valuesHidden } = usePrivacy();
  const muted = useColorModeValue('gray.500', 'whiteAlpha.600');
  const negative = useColorModeValue('red.600', 'red.300');

  const rows = balances ?? [];
  // Coluna que repete o mesmo conteúdo em toda linha não informa nada: sobe para o subtítulo
  // da seção e devolve a largura para as colunas que de fato variam.
  const sharedCustomer = sharedValue(rows, (item) => item.Customer?.name);
  const settlement = sharedSettlement(rows);
  const showRefunded = hasAnyRefund(rows);
  const hasThirdParty = rows.some((item) => ownerId && item.Customer?.id !== ownerId);

  if (!rows.length)
    return (
      <Text color={muted} fontSize="sm">
        Esta venda ainda não gerou lançamentos.
      </Text>
    );

  return (
    <TableContainer>
      <Table size="sm">
        <Thead>
          <Tr>
            <Th>Tipo</Th>
            {!sharedCustomer && <Th>Cliente</Th>}
            <Th isNumeric>Valor</Th>
            {showRefunded && <Th isNumeric>Valor estornado</Th>}
            {!settlement && <Th>Liberação</Th>}
          </Tr>
        </Thead>
        <Tbody>
          {rows.map((item) => {
            const state = settlementStateOf(item);
            const badge = SETTLEMENT_BADGE[state];

            return (
              <Tr key={item.id} opacity={state === 'canceled' ? 0.6 : 1}>
                <Td>
                  <Text>{TypeRender(item.type, item.amount)}</Text>
                  {item.description && (
                    <Text fontSize="xs" color={muted} noOfLines={1}>
                      {item.description}
                    </Text>
                  )}
                </Td>
                {!sharedCustomer && <Td>{item.Customer?.name}</Td>}
                <Td isNumeric>
                  {/* Cor do sinal só com os valores à mostra, senão o vermelho entrega a
                      direção que o texto acabou de esconder. */}
                  <Text color={!valuesHidden && toAmount(item.amount) < 0 ? negative : undefined}>
                    {maskCurrency(item.amount)}
                  </Text>
                </Td>
                {showRefunded && <Td isNumeric>{toAmount(item.amountRefunded) > 0 ? maskCurrency(item.amountRefunded) : '---'}</Td>}
                {!settlement && (
                  <Td>
                    <HStack spacing={2}>
                      <Box>{releaseLabel(state, item.releasedOn)}</Box>
                      <Badge colorScheme={badge.colorScheme}>{badge.label}</Badge>
                    </HStack>
                  </Td>
                )}
              </Tr>
            );
          })}
        </Tbody>
        <Tfoot>
          <Tr>
            <Th>{hasThirdParty ? `Creditado para ${ownerName ?? 'você'}` : 'Total'}</Th>
            {!sharedCustomer && <Th />}
            <Th isNumeric fontSize="sm">
              {maskCurrency(creditedToOwner(rows, ownerId))}
            </Th>
            {showRefunded && <Th />}
            {!settlement && <Th />}
          </Tr>
        </Tfoot>
      </Table>
    </TableContainer>
  );
};

export default function Movement() {
  const { id } = useParams<{ id: string }>();
  const muted = useColorModeValue('gray.500', 'whiteAlpha.600');
  const panelBg = useColorModeValue('gray.50', 'whiteAlpha.100');

  const { data } = useQuery(['getMovement', id], () => getMovement(id));

  const balances = data?.balance;
  const ownerId = data?.Customer?.id;
  const release = earliestRelease(balances);
  const days = settlementDays(data?.orderAt, release);

  const liquid = toAmount(data?.amountLiquid);
  const credited = creditedToOwner(balances, ownerId);
  const refunded = toAmount(data?.amountRefunded);
  // Um centavo de folga: os balances são somados em ponto flutuante.
  const splitLeaves = !!balances?.length && Math.abs(credited - liquid) >= 0.01;

  const feePercent = +data?.feePercent || (100 - (+data?.amountLiquid / +data?.amount) * 100).toFixed(2);

  const settlement = sharedSettlement(balances);
  const compositionCaption = [sharedValue(balances, (item) => item.Customer?.name), settlement && settlementCaption(settlement)]
    .filter(Boolean)
    .join(' · ');

  const identifiers = [
    { title: 'Transação', value: data?.id },
    { title: 'Id da transação', value: data?.transactionCode },
    ...(data?.txId ? [{ title: 'TxId', value: data?.txId }] : []),
  ];

  const details = [
    { title: 'Tipo', content: MovementsTypeDescription[data?.type] },
    ...(data?.type === MovementsType.CREDIT_CARD
      ? [{ title: 'Parcelas', content: `${data?.installmentCount}x` }]
      : []),
    ...(['CREDIT_CARD', 'DEBIT_CARD'].includes(data?.type)
      ? [
          { title: 'Bandeira', content: data?.flagCard },
          {
            title: 'Cartão',
            content: data?.lastDigits ? data.firstDigits + '******' + data?.lastDigits : '---',
          },
        ]
      : []),
    ...(data?.Maquinetas?.number
      ? [{ title: 'Terminal', content: `${data?.Maquinetas?.number} - ${data?.Maquinetas?.name}` }]
      : []),
    ...(data?.payerName ? [{ title: 'Nome do pagador', content: data?.payerName }] : []),
    ...(data?.payerDocument ? [{ title: 'Documento do pagador', content: maskDocument(data?.payerDocument) }] : []),
  ];

  const handleRefresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ['getMovement', id] });
  };

  return (
    <PullToRefresh onRefresh={handleRefresh}>
      <Box>
        <SimpleGrid mb="20px" columns={{ sm: 1 }}>
          <Card px={{ base: 3, md: 5 }} py={4}>
            <Flex direction={{ base: 'column', lg: 'row' }} justify="space-between" gap={5}>
              <Box minW={0}>
                <Text fontSize="sm" color={muted}>
                  Detalhes da transação
                </Text>
                <HStack spacing={3} align="center" mt={1} flexWrap="wrap">
                  <Text fontSize={{ base: '2xl', md: '3xl' }} fontWeight="bold" lineHeight="1.1">
                    {maskCurrency(data?.amount)}
                  </Text>
                  {data?.status && StatusRender(data.status, { withLabel: true })}
                </HStack>
                <Text mt={2} color={muted}>
                  {MovementsTypeDescription[data?.type]}
                  {data?.orderAt && ` · ${DateTime.fromISO(data.orderAt).toFormat('dd/MM/yyyy HH:mm:ss')}`}
                </Text>
                <Text fontWeight="semibold">{data?.Customer?.name}</Text>
              </Box>

              <Stack bg={panelBg} borderRadius="lg" p={4} spacing={2} minW={{ lg: '320px' }}>
                <SummaryRow label="Valor bruto" value={data?.amount} />
                <SummaryRow
                  label={`Taxas${Number.isFinite(+feePercent) ? ` (${feePercent}%)` : ''}`}
                  value={-toAmount(data?.taxes)}
                />
                {refunded > 0 && <SummaryRow label="Devolvido" value={-refunded} />}
                <Divider />
                <SummaryRow label="Valor líquido" value={data?.amountLiquid} strong={!splitLeaves} />
                {splitLeaves && (
                  <>
                    <SummaryRow label="Creditado na sua conta" value={credited} strong />
                    <Text fontSize="xs" color={muted}>
                      O líquido não desconta o split enviado a outra conta.
                    </Text>
                  </>
                )}
                {release && (
                  <Text fontSize="sm" color={muted}>
                    Libera em {DateTime.fromISO(release).toFormat('dd/MM/yyyy')}
                    {days !== null && ` · D+${days}`}
                  </Text>
                )}
              </Stack>
            </Flex>

            <Divider mt={4} />

            <Section title="Composição do valor" caption={compositionCaption}>
              <BalancesForMovement balances={balances} ownerId={ownerId} ownerName={data?.Customer?.name} />
            </Section>

            {!!details.length && (
              <>
                <Divider />
                <Section title="Detalhes">
                  <SimpleGrid columns={{ base: 1, md: 3, xl: 4 }} spacing={4}>
                    {details.map((item) => (
                      <Field key={item.title} title={item.title}>
                        {item.content}
                      </Field>
                    ))}
                  </SimpleGrid>
                </Section>
              </>
            )}

            <Divider />

            <Section title="Identificadores">
              <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} spacing={4}>
                {identifiers.map((item) => (
                  <Field key={item.title} title={item.title}>
                    <CopyableText value={item.value} label={item.title} />
                  </Field>
                ))}
                {data?.chargeId && (
                  <Field title="Cobrança">
                    <Link textDecoration="underline" href={`/access/charge/${data?.chargeId}`} fontSize="sm">
                      Abrir cobrança
                    </Link>
                  </Field>
                )}
              </SimpleGrid>
            </Section>
          </Card>
        </SimpleGrid>
      </Box>
    </PullToRefresh>
  );
}
